import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { GAME_RULES, type GameId } from '@ringmaster/shared';
import { AnimatePresence, motion } from 'framer-motion';
import { createDriver, MOCK_HOST_ID } from '../driver/index.js';
import { useRoom } from '../net/useRoom.js';
import { loadSession } from '../net/session.js';
import { useNarration } from '../audio/useNarration.js';
import { useNightAmbient } from '../audio/useNightAmbient.js';
import { stagePlayer } from '../audio/stagePlayer.js';
import { themeForPhase } from '../theme/phaseTheme.js';
import { TitleScreen, LobbyScreen, StageView } from '../stage/index.js';
import { RoomLostOverlay } from '../ui/RoomLostOverlay.js';
import { HandGameView } from '../hand/index.js';
import { RingmasterBar } from '../ui/index.js';

/**
 * Host (`/host`) — the Ringmaster's device.
 *
 * The host is a SEATED player (admin plays, Mode B) AND owns the Stage audio, so:
 *  - Pre-game: Title (with name input) → Lobby (room code + QR + roster + Start).
 *  - Post-start (DEAL onward): the host sees EXACTLY what a player sees — the same
 *    hand screens driven by their own PrivateView — plus the narrator controls:
 *    a GameHeader (phase label + timer) on top and a RingmasterBar at the bottom.
 *
 * Transport: attaches as a 'hand' surface so the driver delivers this socket's
 * own PrivateView (the live server already sends PRIVATE_STATE to the host's
 * seated socket; the mock needs role='hand' + the host's id). It ALSO issues
 * CLAIM_STAGE so this socket receives NARRATION with real audioIds (Hands are
 * caption-only). Audio is bridged via useNarration(true).
 *
 * Reconnect: `/host` is the fresh-create entry (Title → CREATE_ROOM). Once a
 * room exists we rewrite the URL to `/host/:code` (via replaceState — no route
 * change), so a refresh lands on `/host/:code` and auto-REJOINs from the stored
 * session instead of spawning a brand-new room. Mirrors the phone (`/play`) flow.
 */

/**
 * Game ids the Store tile may launch — DERIVED from `GAME_RULES[id].offered`,
 * never hand-listed. A retired game (The Whisper, 2026-07-29) must not be
 * reachable by hand-typing `?game=whisper` either; this was the last path that
 * still allowed it after the picker, Store and nudge were closed off.
 */
const KNOWN_GAME_IDS: readonly GameId[] = Object.values(GAME_RULES)
  .filter((r) => r.offered)
  .map((r) => r.id as GameId);

/**
 * Read the `?game=<id>` param the Store tile appends when launching a lobby.
 * Returns a validated GameId or undefined (the server defaults undefined →
 * 'bloodmoon'). Kept outside the component so the create handler can call it
 * lazily inside the user's tap.
 */
function readGameParam(): GameId | undefined {
  if (typeof window === 'undefined') return undefined;
  const raw = new URLSearchParams(window.location.search).get('game');
  return (KNOWN_GAME_IDS as readonly string[]).includes(raw ?? '')
    ? (raw as GameId)
    : undefined;
}

export default function Host() {
  // Present only on `/host/:code` (the reconnect entry). `/host` has no param.
  const { code: codeParam } = useParams();
  // Select each field individually to avoid creating a new object on every
  // render (which would trigger React's getSnapshot infinite-loop guard).
  const publicView = useRoom((s) => s.publicView);
  const privateView = useRoom((s) => s.privateView);
  const narration = useRoom((s) => s.narration);
  const playerId = useRoom((s) => s.playerId);
  const isHost = useRoom((s) => s.isHost);
  const code = useRoom((s) => s.code);
  const send = useRoom((s) => s.send);
  const attach = useRoom((s) => s.attach);
  // Server restarted / room swept — the board on screen is dead (see ui/RoomLostOverlay).
  const roomLost = useRoom((s) => s.roomLost);

  const { isSpeaking, unlock, caption } = useNarration(true);
  // Stage-only ambient soundscape, driven by the public phase.
  useNightAmbient(true, publicView?.phase);

  // Narrator playback controls. Pause is SERVER-authoritative (freezes the
  // phase timer for everyone); the local stagePlayer follows it via the effect
  // below. Mute stays host-local.
  const paused = publicView?.paused ?? false;
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (paused) stagePlayer.pause();
    else stagePlayer.resume();
  }, [paused]);

  // Attach the driver once on mount. As a 'hand' surface the host receives its
  // own PrivateView; the mock host is p_ana (ignored by the live driver).
  //
  // On `/host/:code` with a stored session we REJOIN the existing room (refresh
  // recovery); otherwise we attach fresh for the Title → CREATE_ROOM flow.
  useEffect(() => {
    const stored = codeParam ? loadSession(codeParam.toUpperCase()) : null;
    if (stored) {
      const d = createDriver(stored.playerId);
      // claimStage: the host device IS the audio surface — a reconnect must
      // re-claim it (see LiveSocketDriver / room.stageClaims).
      attach(d, { role: 'hand', code: stored.code, sessionToken: stored.sessionToken, claimStage: true });
      // Explicit REJOIN (mock relies on it; live also tolerates it).
      setTimeout(() => {
        useRoom.getState().send({ type: 'REJOIN', code: stored.code, sessionToken: stored.sessionToken });
      }, 0);
    } else {
      const d = createDriver(MOCK_HOST_ID);
      attach(d, { role: 'hand', claimStage: true });
    }
    return () => {
      useRoom.getState().detach();
    };
    // codeParam is captured on first mount; the effect self-guards against reruns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attach]);

  // Once a room exists (fresh create), rewrite the URL to /host/:code WITHOUT a
  // route change (replaceState — react-router ignores it), so a refresh reconnects.
  useEffect(() => {
    if (code && !codeParam && typeof window !== 'undefined') {
      window.history.replaceState(null, '', `/host/${code}`);
    }
  }, [code, codeParam]);

  // Once the room acknowledges us (JOINED → playerId), CLAIM_STAGE so this socket
  // starts receiving NARRATION with audioId (Hands get caption-only).
  const claimedStage = useRef(false);
  useEffect(() => {
    if (playerId && !claimedStage.current) {
      claimedStage.current = true;
      send({ type: 'CLAIM_STAGE' });
    }
  }, [playerId, send]);

  const phase = publicView?.phase;
  const theme = phase
    ? themeForPhase(phase)
    : { bgGradient: 'linear-gradient(180deg, #000 0%, #320001 100%)', accent: '#990000' };

  function handleCreateRoom(name: string) {
    // Creating the room is a user gesture — unlock audio here so the lobby music
    // can begin the moment we land in the lobby (iOS needs a gesture to start).
    unlock();
    // Which game to seat this room with — carried from the Store tile as
    // `/host?game=<id>`. Unknown / absent → the server defaults to 'bloodmoon'.
    send({ type: 'CREATE_ROOM', name, gameId: readGameParam() });
  }

  function handleSetNarratorOnly(narratorOnly: boolean) {
    send({ type: 'SET_NARRATOR_ONLY', narratorOnly });
  }

  function handleEndDiscussion() {
    send({ type: 'HOST_ADVANCE' });
  }

  function handlePlayAgain() {
    // Rematch restarts the SAME game with no lobby stop: PLAY_AGAIN resets the
    // room to LOBBY (roster + gameId kept) and START_GAME deals immediately.
    // Same socket = in-order delivery, and the server handles both
    // synchronously. If the roster shrank below minPlayers while the game ran,
    // START is a silent engine no-op (Blood Moon and One Night narrate
    // NEED_PLAYERS; Whisper rejects it silently) and the
    // table simply sits in the lobby — exactly today's behaviour, now explained.
    send({ type: 'PLAY_AGAIN' });
    send({ type: 'START_GAME' });
  }

  function handleStart() {
    unlock(); // iOS audio unlock inside the gesture
    send({ type: 'START_GAME' });
  }

  function handleSetConfig(secs: number) {
    send({ type: 'SET_CONFIG', discussionSecs: secs });
  }

  function handleTogglePause() {
    // Optimistically pause the audio inside the tap gesture; the server echoes
    // the paused state back via PublicView (freezing every device's timer).
    const next = !paused;
    if (next) stagePlayer.pause();
    else stagePlayer.resume();
    send({ type: 'HOST_SET_PAUSED', paused: next });
  }

  function handleToggleMute() {
    setMuted((m) => {
      const next = !m;
      stagePlayer.setMuted(next);
      return next;
    });
  }

  function handleKick(id: string) {
    send({ type: 'KICK_PLAYER', playerId: id });
  }

  const myName = publicView?.players.find((p) => p.id === playerId)?.name ?? '';

  // Narrator-only: this host device runs the game but was dropped from the roster
  // (server REMOVE_PLAYER on the SET_NARRATOR_ONLY toggle). Detected purely from
  // the public view so it survives reconnects — the host keeps its id (playerId
  // === hostId) for permissions, but no longer appears among the players.
  const amNarrator =
    !!playerId && !!publicView && !publicView.players.some((p) => p.id === playerId);

  if (roomLost) return <RoomLostOverlay />;

  // ── Pre-game (Title + Lobby) — public stage layout ───────────────────────────
  const inPreGame = !playerId || !publicView || phase === undefined || phase === 'LOBBY';

  if (inPreGame) {
    // Title + Lobby own their own SceneStack LOBBY backdrop now (lantern-lit tent
    // + moon), so the Host wrapper is just a full-bleed positioned stage — no extra
    // gradient (would double-background) and no maxWidth clamp (the screens size
    // their own content). The title↔lobby crossfade stays.
    return (
      <div style={{ position: 'relative', minHeight: '100dvh', overflow: 'hidden', color: 'var(--text-primary)' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={!playerId || !publicView || phase === undefined ? 'title' : 'lobby'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            style={{ position: 'absolute', inset: 0 }}
          >
            {!playerId || !publicView || phase === undefined ? (
              <TitleScreen onCreateRoom={handleCreateRoom} />
            ) : (
              <LobbyScreen
                view={publicView}
                onStart={handleStart}
                onSetConfig={handleSetConfig}
                isHost={isHost}
                narratorOnly={amNarrator}
                onSetNarratorOnly={handleSetNarratorOnly}
                myId={playerId ?? undefined}
                onKick={handleKick}
                muted={muted}
                onToggleMute={handleToggleMute}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // ── Post-start (narrator-only) — the host runs the game on a TV/Stage ────────
  // Not dealt in: render the PUBLIC Stage view (no private hand) + narrator bar.
  if (amNarrator) {
    return (
      <div style={{ position: 'relative', minHeight: '100dvh' }}>
        <StageView
          publicView={publicView}
          caption={caption}
          isSpeaking={isSpeaking && !paused}
          onEndDiscussion={handleEndDiscussion}
          onPlayAgain={handlePlayAgain}
        />
        <RingmasterBar
          paused={paused}
          muted={muted}
          speaking={isSpeaking && !paused}
          phase={phase}
          onTogglePause={handleTogglePause}
          onToggleMute={handleToggleMute}
          onSkipPhase={handleEndDiscussion}
        />
      </div>
    );
  }

  // ── Post-start — the host plays like any player, + narrator controls ─────────
  // If we somehow lack a private view (transient), show a minimal loading state.
  if (!privateView) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          background: theme.bgGradient,
          color: 'var(--text-secondary)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        Dealing the roles…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <HandGameView
        publicView={publicView}
        privateView={privateView}
        narration={narration}
        myName={myName}
        showHeader
        bottomSlot={
          <RingmasterBar
            paused={paused}
            muted={muted}
            speaking={isSpeaking && !paused}
            phase={phase}
            onTogglePause={handleTogglePause}
            onToggleMute={handleToggleMute}
            onSkipPhase={handleEndDiscussion}
          />
        }
      />
    </div>
  );
}
